import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import PartnerPackagesSubnav from '../components/PartnerPackagesSubnav';
import {
  PartnerEmptyState,
  PartnerLoading,
  PartnerPageHeader,
  PartnerPageShell,
  partnerLinkSx,
  partnerOutlineBtnSx,
  partnerOxbloodBtnSx,
} from '../components/PartnerListChrome';
import { DOCUMENT } from '../constants/documentTheme';

const emptyForm = {
  company_name: '',
  contact_name: '',
  email: '',
  phone: '',
  notes: '',
};

const PartnerDmcs = () => {
  const { t } = useTranslation();
  const [dmcs, setDmcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadDmcs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/partner-packages/dmcs', { usePartnerAuth: true });
      setDmcs(response.data?.dmcs || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.dmcsLoadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadDmcs();
  }, [loadDmcs]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (dmc) => {
    setEditingId(dmc.id);
    setForm({
      company_name: dmc.company_name || '',
      contact_name: dmc.contact_name || '',
      email: dmc.email || '',
      phone: dmc.phone || '',
      notes: dmc.notes || '',
    });
    setFormError('');
    setDialogOpen(true);
  };

  const saveDmc = async () => {
    if (!form.company_name.trim() || (!form.email.trim() && !form.phone.trim())) {
      setFormError(t('createPackage.dmcContactRequired'));
      return;
    }
    try {
      setSaving(true);
      setFormError('');
      if (editingId) {
        await api.put(`/partner-packages/dmcs/${editingId}`, form, { usePartnerAuth: true });
      } else {
        await api.post('/partner-packages/dmcs', form, { usePartnerAuth: true });
      }
      setDialogOpen(false);
      setNotice(t('partnerLists.dmcSaved'));
      await loadDmcs();
    } catch (err) {
      setFormError(err.response?.data?.error || t('createPackage.dmcSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const deleteDmc = async () => {
    if (!pendingDelete) return;
    try {
      setSaving(true);
      await api.delete(`/partner-packages/dmcs/${pendingDelete.id}`, { usePartnerAuth: true });
      setPendingDelete(null);
      setNotice(t('partnerLists.dmcDeleted'));
      await loadDmcs();
    } catch (err) {
      setError(err.response?.data?.error || t('partnerLists.dmcDeleteFailed'));
      setPendingDelete(null);
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartnerPageShell
      error={error}
      onClearError={() => setError('')}
      success={notice}
      onClearSuccess={() => setNotice('')}
    >
      <PartnerPageHeader
        title={t('partnerLists.dmcsTitle')}
        subtitle={t('partnerLists.dmcsSub')}
        actions={(
          <Button sx={partnerOxbloodBtnSx} onClick={openCreate} data-testid="partner-dmcs-add">
            {t('createPackage.addDmc')}
          </Button>
        )}
      />
      <PartnerPackagesSubnav />

      {loading ? (
        <PartnerLoading />
      ) : dmcs.length === 0 ? (
        <PartnerEmptyState
          title={t('partnerLists.dmcsEmptyTitle')}
          body={t('partnerLists.dmcsEmptyBody')}
        />
      ) : (
        <Box sx={{ mt: 2 }} data-testid="partner-dmcs-list">
          {dmcs.map((dmc) => (
            <Box
              key={dmc.id}
              data-testid={`partner-dmc-row-${dmc.id}`}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                py: 1.75,
                borderBottom: `1px solid ${DOCUMENT.rule}`,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink }}>
                  {dmc.company_name}
                </Typography>
                <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2, mt: 0.4 }}>
                  {[dmc.contact_name, dmc.email, dmc.phone].filter(Boolean).join(' · ')}
                </Typography>
                {dmc.notes ? (
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mt: 0.6, whiteSpace: 'pre-wrap' }}>
                    {dmc.notes}
                  </Typography>
                ) : null}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, pt: 0.6 }}>
                <Box component="button" type="button" onClick={() => openEdit(dmc)} sx={partnerLinkSx}>
                  {t('createPackage.editDmc')}
                </Box>
                <Box component="button" type="button" onClick={() => setPendingDelete(dmc)} sx={partnerLinkSx}>
                  {t('common.delete')}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={dialogOpen}
        onClose={() => !saving && setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: DOCUMENT.canvas,
            border: `1px solid ${DOCUMENT.rule}`,
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {editingId ? t('createPackage.editDmc') : t('createPackage.addDmc')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {formError ? (
            <Typography sx={{ color: DOCUMENT.oxblood, fontSize: 13 }}>{formError}</Typography>
          ) : null}
          <TextField
            label={t('createPackage.dmcCompany')}
            value={form.company_name}
            onChange={(event) => setForm((prev) => ({ ...prev, company_name: event.target.value }))}
            required
            variant="standard"
            inputProps={{ 'data-testid': 'partner-dmc-company' }}
          />
          <TextField
            label={t('createPackage.dmcContact')}
            value={form.contact_name}
            onChange={(event) => setForm((prev) => ({ ...prev, contact_name: event.target.value }))}
            variant="standard"
          />
          <TextField
            label={t('createPackage.dmcEmail')}
            value={form.email}
            onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
            variant="standard"
            inputProps={{ 'data-testid': 'partner-dmc-email' }}
          />
          <TextField
            label={t('createPackage.dmcPhone')}
            value={form.phone}
            onChange={(event) => setForm((prev) => ({ ...prev, phone: event.target.value }))}
            variant="standard"
          />
          <TextField
            label={t('createPackage.dmcNotes')}
            value={form.notes}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            variant="standard"
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving} sx={partnerOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button onClick={saveDmc} disabled={saving} sx={partnerOxbloodBtnSx} data-testid="partner-dmc-save">
            {t('createPackage.saveDmc')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(pendingDelete)}
        onClose={() => !saving && setPendingDelete(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 0,
            bgcolor: DOCUMENT.canvas,
            border: `1px solid ${DOCUMENT.rule}`,
            boxShadow: 'none',
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: DOCUMENT.display, fontWeight: 400 }}>
          {t('partnerLists.deleteDmcTitle')}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: DOCUMENT.ink2 }}>
            {t('partnerLists.deleteDmcBody', { name: pendingDelete?.company_name || '' })}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPendingDelete(null)} disabled={saving} sx={partnerOutlineBtnSx}>
            {t('common.cancel')}
          </Button>
          <Button onClick={deleteDmc} disabled={saving} sx={partnerOxbloodBtnSx} data-testid="partner-dmc-confirm-delete">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </PartnerPageShell>
  );
};

export default PartnerDmcs;
